import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as api from '../api';
import { Icon } from '../components/Icon';
import { Button, cx } from '../components/ui';

const KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', 'clear', '0', 'back'];

/**
 * Passcode gate. A keypad rather than a text field because the primary device is a
 * wall-mounted tablet with no keyboard — but a hidden input keeps hardware keyboards and
 * password managers working on phones and desktops.
 */
export function Login() {
  const qc = useQueryClient();
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);

  const { data: setup, isLoading } = useQuery({ queryKey: ['setup'], queryFn: api.getSetupStatus });
  const needsSetup = setup?.needsSetup === true;

  const submit = useMutation({
    mutationFn: (passcode: string) => (needsSetup ? api.doSetup(passcode) : api.login(passcode)),
    onSuccess: async () => {
      setError(null);
      await qc.invalidateQueries();
    },
    onError: (err: Error) => {
      setError(err.message);
      setCode('');
    },
  });

  const press = (k: string): void => {
    setError(null);
    if (k === 'clear') return setCode('');
    if (k === 'back') return setCode((c) => c.slice(0, -1));
    if (code.length >= 12) return;
    setCode((c) => c + k);
  };

  const canSubmit = code.length >= 4 && !submit.isPending;
  const go = (): void => {
    if (canSubmit) submit.mutate(code);
  };

  if (isLoading) return <div className="h-full" />;

  return (
    <div className="flex h-full flex-col items-center justify-center gap-8 px-6">
      <div className="text-center">
        <span className="eyebrow">{needsSetup ? 'First run' : 'Locked'}</span>
        <h1 className="mt-1.5 font-display text-[32px] leading-tight text-primary">
          {needsSetup ? 'Welcome to Pacer' : 'Pacer'}
        </h1>
        <p className="mt-1 text-[13px] text-secondary">
          {needsSetup ? 'Choose a passcode for the household' : 'Enter the household passcode'}
        </p>
      </div>

      {/* Dots, not characters — the wall is readable from across the room. */}
      <div className="flex h-4 items-center gap-2.5" aria-live="polite">
        {code.length === 0 ? (
          <span className="font-mono text-[12px] text-muted">····</span>
        ) : (
          Array.from({ length: code.length }).map((_, i) => (
            <span key={i} className="size-2.5 rounded-full bg-accent" />
          ))
        )}
      </div>

      <div className="grid grid-cols-3 gap-2.5">
        {KEYS.map((k) => (
          <button
            key={k}
            onClick={() => press(k)}
            aria-label={k === 'back' ? 'Backspace' : k === 'clear' ? 'Clear' : k}
            className={cx(
              'flex size-[76px] items-center justify-center rounded-lg border border-border-subtle bg-surface',
              'font-mono text-[22px] text-primary transition-colors duration-150',
              'hover:border-border hover:bg-surface-high active:bg-surface-high',
            )}
          >
            {k === 'back' ? (
              <Icon name="chevron-left" size={20} />
            ) : k === 'clear' ? (
              <Icon name="close" size={18} />
            ) : (
              k
            )}
          </button>
        ))}
      </div>

      <div className="h-5 text-center">
        {error && <p className="font-mono text-[12px] text-danger">{error}</p>}
        {needsSetup && !error && (
          <p className="font-mono text-[12px] text-muted">At least 4 digits. Everyone at home shares it.</p>
        )}
      </div>

      <Button variant="primary" size="lg" onClick={go} disabled={!canSubmit} className="w-64">
        {submit.isPending ? 'Checking…' : needsSetup ? 'Create household' : 'Unlock'}
      </Button>

      <input
        autoFocus
        className="sr-only"
        value={code}
        inputMode="numeric"
        onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 12))}
        onKeyDown={(e) => e.key === 'Enter' && go()}
        aria-label="Household passcode"
      />
    </div>
  );
}
